import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user came from signup flow via query parameters
  const urlParams = new URLSearchParams(location.search);
  const fromSignup = urlParams.get('from') === 'signup';

  const handleBack = () => {
    if (fromSignup) {
      // Navigate back to signup if user came from signup flow
      navigate('/signup');
    } else if (window.history.length > 1) {
      // Use browser back if there's history
      window.history.back();
    } else {
      // Fallback to home page
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors "
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {fromSignup ? 'Back' : 'Back'}
            </button>
            <div className="text-center flex-1">
              <img 
                src="/images/noBgColor.png" 
                alt="Silas Logo" 
                className="h-8 w-auto object-contain mx-auto"
              />
            </div>
            <div className="w-16"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your privacy and data security are our top priorities. Learn how we protect your sensitive health information.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Effective Date: July 21, 2025
          </p>
        </div>

        {/* Content Grid */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-12 lg:px-12 lg:py-16 prose prose-lg max-w-none">
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Silas Health Group LLC ("Silas", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy describes how we collect, use, and share your personal information when you use our website, services, mobile applications, or other products under the Silas brand (collectively, the "Services").
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Contact Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-700">
                  <strong>Silas Health Group LLC</strong><br />
                  2405 W Princeton Street, Suite 9<br />
                  Orlando, FL 33326<br />
                  <strong>Email:</strong> support@silasai.ai
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect the following categories of personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Information You Provide:</strong> Name, email address, phone number, payment information, mental health-related responses, feedback, and messages.</li>
                <li><strong>Automatically Collected Data:</strong> Device information, IP address, browser type, access times, and usage data.</li>
                <li><strong>Third-Party Data:</strong> If you link Silas to a third-party account or service (e.g., Apple, Google), we may receive information from those services as permitted by their terms.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide and personalize our Services;</li>
                <li>Communicate with you, including sending updates and support;</li>
                <li>Improve user experience and product functionality;</li>
                <li>Ensure legal and regulatory compliance;</li>
                <li>Protect against fraud, abuse, and other malicious activity;</li>
                <li>Analyze trends and usage for business development.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Sharing of Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Service Providers:</strong> Third parties who perform services on our behalf (e.g., cloud hosting, messaging platforms like Twilio).</li>
                <li><strong>Legal Requirements:</strong> If required by law, regulation, or legal process.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or asset sale.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use industry-standard security protocols to protect your data, including encryption, access control, and regular audits. However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Access the data we hold about you;</li>
                <li>Correct inaccurate data;</li>
                <li>Request deletion of your data;</li>
                <li>Object to processing or withdraw consent;</li>
                <li>Lodge a complaint with a data protection authority.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                To exercise any of these rights, contact us at support@silasai.ai.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Silas is not intended for children under 13. We do not knowingly collect personal data from children. If you believe we have collected such data, contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Retention of Data</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal data for as long as necessary to provide our Services, fulfill legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this policy from time to time. We will notify you of significant changes via the app, website, or email. Continued use of the Services after changes means you accept the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong>Silas Health Group LLC</strong><br />
                  2405 W Princeton Street, Suite 9<br />
                  Orlando, FL 33326<br />
                  <strong>Email:</strong> support@silasai.ai
                </p>
              </div>
            </section>
          </div>
        </div>


      </div>
    </div>
  );
};

export default PrivacyPolicy; 